import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useSharePoint } from '../../contexts/SharePointContext';
import { supabase } from '../../lib/supabase';
import { documentService } from '../../services/documentService';
import settingsService from '../../services/settingsService';
import ConfirmationModal from './components/ConfirmationModal';
import CreateFolderModal from './components/CreateFolderModal';
import DocumentBaseTree from './components/DocumentBaseTree';
import DocumentGrid from './components/DocumentGrid';
import DocumentToolbar from './components/DocumentToolbar';
import DocumentUpload from './components/DocumentUpload';
import RenameModal from './components/RenameModal';
import SharePointConfigModal from './components/SharePointConfigModal';

const DocumentBaseManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isConfigured: sharePointConfigured, selectedSite, selectedDrive } = useSharePoint();
  
  // Add missing state declarations at the beginning
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [documentBases, setDocumentBases] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('base');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', item: null, items: [], loading: false });
  const [renameModal, setRenameModal] = useState({ isOpen: false, item: null, loading: false });
  const [showStorageConfigModal, setShowStorageConfigModal] = useState(false);
  const [currentStorageStrategy, setCurrentStorageStrategy] = useState('supabase');
  const [allDocuments, setAllDocuments] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // FIXED: Initialize storage strategy on mount, respecting localStorage priority
  useEffect(() => {
    // Check if there's a persisted strategy in localStorage first
    const persistedStrategy = localStorage.getItem('zentri_storage_strategy');
    
    if (persistedStrategy === 'sharepoint' || persistedStrategy === 'supabase') {
      // Use persisted strategy if valid
      console.log('[STORAGE_INIT] Using persisted strategy from localStorage:', persistedStrategy);
      documentService?.setStorageStrategy(persistedStrategy);
      setCurrentStorageStrategy(persistedStrategy);
    } else if (sharePointConfigured && selectedSite && selectedDrive) {
      // Only set SharePoint if no valid persisted strategy exists
      console.log('[STORAGE_INIT] No persisted strategy, SharePoint is configured, setting to sharepoint');
      documentService?.setStorageStrategy('sharepoint');
      setCurrentStorageStrategy('sharepoint');
    } else {
      // Default to Supabase only if nothing else is configured
      console.log('[STORAGE_INIT] No persisted strategy, SharePoint not configured, using supabase');
      documentService?.setStorageStrategy('supabase');
      setCurrentStorageStrategy('supabase');
    }
  }, [sharePointConfigured, selectedSite, selectedDrive]);

  // Load default view mode from system settings on mount
  useEffect(() => {
    const loadDefaultView = async () => {
      try {
        // Get current authenticated user
        const { data: { user }, error: authError } = await supabase?.auth?.getUser();
        
        if (authError || !user?.id) {
          console.warn('No authenticated user found, using default view mode');
          return;
        }

        // Fetch settings with proper parameters
        const settings = await settingsService?.getSettings(user?.id, 'interface');
        
        // Extract layout settings and get default view
        const layoutSettings = settings?.layout;
        const defaultView = layoutSettings?.defaultView;
        
        if (defaultView && (defaultView === 'grid' || defaultView === 'list')) {
          console.log('Applying default view from settings:', defaultView);
          setViewMode(defaultView);
        } else {
          console.log('No default view setting found, using grid view');
        }
      } catch (err) {
        console.error('Error loading default view setting:', err);
        // Keep default 'grid' view if settings can't be loaded
      }
    };

    loadDefaultView();
  }, []);

  // Load documents when folder changes
  useEffect(() => {
    loadDocuments();
  }, [selectedFolder, refreshKey]);

  // Add initial data load effect
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await loadDocumentBases();
        if (documentBases?.length > 0 && !selectedFolder) {
          setSelectedFolder({ ...documentBases?.[0], type: 'base' });
        }
      } catch (err) {
        setError('Failed to initialize: ' + err?.message);
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // Add missing function declarations
  const loadDocumentBases = async () => {
    try {
      const bases = await documentService?.getDocumentBases();
      setDocumentBases(bases || []);
      await loadAllFolders();
    } catch (err) {
      setError('Failed to load document bases: ' + err?.message);
    }
  };

  const loadAllFolders = async () => {
    try {
      const allFolders = await documentService?.getAllFolders();
      setFolders(allFolders || []);
    } catch (err) {
      setError('Failed to load folders: ' + err?.message);
    }
  };

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSelectedFolder(item);
  };

  const loadDocuments = async () => {
    if (!selectedFolder) return;
    
    try {
      // Add cache-busting timestamp parameter
      const docs = await documentService?.getDocuments(
        selectedFolder?.documentBaseId || selectedFolder?.id,
        selectedFolder?.type === 'folder' ? selectedFolder?.id : null
      );
      
      // Force state update with new array reference
      setDocuments([...docs]);
    } catch (err) {
      setError('Failed to load documents: ' + err?.message);
    }
  };

  const handleCreateFolder = (type, parentItem = null) => {
    setShowCreateModal(true);
    setCreateModalType(type);
    setItemToRename(null);
    setItemToDelete(null);
  };

  const handleCreateItem = async (newItem) => {
    try {
      if (newItem?.type === 'base') {
        await documentService?.createDocumentBase(
          newItem?.name, 
          newItem?.description, 
          newItem?.isPublic
        );
        await loadDocumentBases();
      } else if (newItem?.type === 'folder') {
        await documentService?.createFolder(
          newItem?.name,
          newItem?.description,
          selectedFolder?.documentBaseId || selectedFolder?.id,
          selectedFolder?.type === 'folder' ? selectedFolder?.id : null
        );
        // Refresh all folders to update the tree structure
        await loadAllFolders();
      }
      setIsCreateFolderOpen(false);
    } catch (err) {
      setError('Failed to create item: ' + err?.message);
    }
  };

  // Enhanced delete with role-based validation and confirmation
  const handleDeleteItem = async (item) => {
    if (!item) return;
    
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      item: item,
      items: [],
      loading: false
    });
  };

  const confirmDelete = async () => {
    const { item, items, type } = confirmModal;
    
    console.log('[CONFIRM_DELETE] Starting deletion process', { type, itemCount: items?.length || 1 });
    
    // Handle bulk delete
    if (type === 'bulk-delete' && items?.length > 0) {
      setConfirmModal(prev => ({ ...prev, loading: true }));
      
      try {
        console.log('[BULK_DELETE] Deleting', items?.length, 'documents');
        
        // Delete all selected documents with validation
        const deletePromises = items?.map(async (doc) => {
          try {
            console.log('[BULK_DELETE] Deleting document:', doc?.name);
            await documentService?.deleteDocumentWithValidation(doc?.id, doc?.filePath);
            return { success: true, id: doc?.id, name: doc?.name };
          } catch (err) {
            console.error('[BULK_DELETE] Failed to delete document:', doc?.name, err);
            return { success: false, id: doc?.id, name: doc?.name, error: err?.message };
          }
        });
        
        const results = await Promise.allSettled(deletePromises);
        const successCount = results?.filter(r => r?.status === 'fulfilled' && r?.value?.success)?.length;
        const failCount = results?.length - successCount;
        
        console.log('[BULK_DELETE] Deletion complete:', { successCount, failCount });
        
        if (failCount > 0) {
          const failedDocs = results
            ?.filter(r => r?.status === 'rejected' || !r?.value?.success)
            ?.map(r => r?.value?.name || 'Unknown')
            ?.join(', ');
          
          setError(`Failed to delete ${failCount} document(s): ${failedDocs}`);
        } else {
          setSuccessMessage(`Successfully deleted ${successCount} document${successCount !== 1 ? 's' : ''}`);
          setTimeout(() => setSuccessMessage(''), 3000);
        }
        
        // Force complete refresh with new key
        setRefreshKey(prev => prev + 1);
        await loadDocuments();
        setSelectedDocuments([]);
        setConfirmModal({ isOpen: false, type: '', item: null, items: [], loading: false });
      } catch (err) {
        console.error('[BULK_DELETE] Unexpected error:', err);
        setError(err?.message || 'Failed to delete documents');
        setConfirmModal(prev => ({ ...prev, loading: false }));
      }
      return;
    }

    // Handle single item delete
    if (!item) {
      console.warn('[CONFIRM_DELETE] No item provided');
      return;
    }

    setConfirmModal(prev => ({ ...prev, loading: true }));
    console.log('[SINGLE_DELETE] Deleting item:', item?.name, 'type:', item?.type);

    try {
      if (item?.type === 'base') {
        console.log('[SINGLE_DELETE] Deleting document base');
        await documentService?.deleteDocumentBaseWithValidation(item?.id);
        setSuccessMessage(`Successfully deleted document base "${item?.name}"`);
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Force complete refresh
        setRefreshKey(prev => prev + 1);
        await loadDocumentBases();
        
        // Reset selection if deleted item was selected
        if (selectedFolder?.id === item?.id) {
          const remaining = documentBases?.filter(base => base?.id !== item?.id);
          setSelectedFolder(remaining?.[0] ? { ...remaining?.[0], type: 'base' } : null);
        }
      } else if (item?.type === 'document') {
        console.log('[SINGLE_DELETE] Deleting document');
        await documentService?.deleteDocumentWithValidation(item?.id, item?.filePath);
        setSuccessMessage(`Successfully deleted document "${item?.name}"`);
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Force complete refresh with new key
        setRefreshKey(prev => prev + 1);
        
        // Clear documents state completely before reloading
        setDocuments([]);
        
        // Small delay to ensure state is fully cleared
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Reload documents
        await loadDocuments();
        setSelectedDocuments(prev => prev?.filter(doc => doc?.id !== item?.id));
        console.log('[SINGLE_DELETE] Document deleted successfully, UI refreshed');
      }
      
      setConfirmModal({ isOpen: false, type: '', item: null, items: [], loading: false });
    } catch (err) {
      console.error('[CONFIRM_DELETE] Error:', err);
      setError(err?.message || 'Failed to delete item');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Enhanced rename with role-based validation and modal
  const handleRenameItem = async (item) => {
    if (!item) return;
    
    setRenameModal({
      isOpen: true,
      item: item,
      loading: false
    });
  };

  const confirmRename = async (newName) => {
    const { item } = renameModal;
    if (!item || !newName?.trim()) return;

    setRenameModal(prev => ({ ...prev, loading: true }));

    try {
      if (item?.type === 'base') {
        await documentService?.renameDocumentBaseWithValidation(item?.id, newName);
        await loadDocumentBases();
      } else if (item?.type === 'document') {
        await documentService?.renameDocumentWithValidation(item?.id, newName);
        await loadDocuments();
      }
      
      setRenameModal({ isOpen: false, item: null, loading: false });
    } catch (err) {
      console.error('Rename error:', err);
      setError(err?.message || 'Failed to rename item');
      setRenameModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleVisibility = async (item) => {
    if (item?.type !== 'base') return;

    try {
      await documentService?.updateDocumentBase(item?.id, { 
        isPublic: !item?.isPublic 
      });
      await loadDocumentBases();
    } catch (err) {
      setError('Failed to update visibility: ' + err?.message);
    }
  };

  const handleUpload = async (file, targetFolder) => {
    try {
      // Determine document base ID
      const documentBaseId = selectedFolder?.documentBaseId || selectedFolder?.id;
      
      // Determine folder ID - only use if it's actually a folder
      let folderId = null;
      if (targetFolder && targetFolder?.type === 'folder') {
        folderId = targetFolder?.id;
      } else if (selectedFolder && selectedFolder?.type === 'folder') {
        folderId = selectedFolder?.id;
      }
      
      // Ensure folderId is not the same as documentBaseId (common mistake)
      if (folderId === documentBaseId) {
        folderId = null;
      }
      
      await documentService?.uploadDocument(
        file,
        documentBaseId,
        folderId,
        ''
      );
      await loadDocuments();
      setShowUploadModal(false);
    } catch (err) {
      setError('Failed to upload document: ' + err?.message);
    }
  };

  const handleDocumentSelect = async (document) => {
    if (!document?.id) {
      setError('Invalid document selected');
      return;
    }
    
    // UPDATED: Directly download/open document instead of navigating to editor
    try {
      console.log('[DOCUMENT_OPEN] Opening document:', document?.name);
      
      // Get the document URL based on storage type
      const url = await documentService?.getDocumentUrl(
        document?.filePath,
        document?.storageType || 'supabase',
        document?.sharePointItemId
      );
      
      if (!url) {
        throw new Error('Failed to generate document URL');
      }
      
      console.log('[DOCUMENT_OPEN] Document URL generated, opening in new tab');
      
      // Open document in new tab (browser will handle download/preview)
      window.open(url, '_blank', 'noopener,noreferrer');
      
    } catch (err) {
      console.error('[DOCUMENT_OPEN] Error opening document:', err);
      setError(`Failed to open document: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleDocumentEdit = (document) => {
    if (!document?.id) {
      setError('Cannot edit document: Invalid document selected');
      return;
    }
    
    // Validate document ID format (should be UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(document?.id)) {
      setError('Cannot edit document: Invalid document ID format');
      return;
    }
    
    // Navigate to document editor with proper document ID
    window.location.href = `/document-editor?id=${document?.id}`;
  };

  // Enhanced document delete with validation
  const handleDocumentDelete = async (document) => {
    handleDeleteItem({ ...document, type: 'document' });
  };

  // Add document rename handler
  const handleDocumentRename = (document) => {
    handleRenameItem({ ...document, type: 'document' });
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments?.length === 0) {
      setError('No documents selected for deletion');
      return;
    }

    // Check if any selected documents are from SharePoint
    const sharePointDocs = selectedDocuments?.filter(doc => {
      const fullDoc = allDocuments?.find(d => d?.id === doc);
      return fullDoc?.storageType === 'sharepoint';
    });

    if (sharePointDocs?.length > 0) {
      const confirmMessage = `⚠️ SharePoint Documents Detected\n\n` +
        `${sharePointDocs?.length} of the selected documents are stored in SharePoint.\n\n` +
        `If SharePoint integration is not properly configured, these deletions may fail.\n\n` +
        `Do you want to proceed with deletion?`;
      
      const confirmed = window?.confirm(confirmMessage);
      if (!confirmed) {
        return;
      }
    }

    setIsDeleting(true);
    const results = await Promise?.allSettled(
      selectedDocuments?.map(docId => {
        const doc = allDocuments?.find(d => d?.id === docId);
        return documentService?.deleteDocumentWithValidation(docId, doc?.filePath);
      })
    );

    // Process results
    const successCount = results?.filter(r => r?.status === 'fulfilled' && r?.value?.success)?.length;
    const failCount = results?.length - successCount;
    
    console.log('[BULK_DELETE] Deletion complete:', { successCount, failCount });
    
    if (failCount > 0) {
      const failedDocs = results
        ?.filter(r => r?.status === 'rejected' || !r?.value?.success)
        ?.map(r => r?.value?.name || 'Unknown')
        ?.join(', ');
      
      setError(`Failed to delete ${failCount} document(s): ${failedDocs}`);
    } else {
      setSuccessMessage(`Successfully deleted ${successCount} document${successCount !== 1 ? 's' : ''}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
    
    // Force complete refresh with new key
    setRefreshKey(prev => prev + 1);
    await loadDocuments();
    setSelectedDocuments([]);
    setConfirmModal({ isOpen: false, type: '', item: null, items: [], loading: false });
    setIsDeleting(false);
  };

  const handleRefresh = async () => {
    console.log('[REFRESH] Starting data refresh');
    
    // Clear any cached state to prevent stale data issues
    setDocuments([]);
    setFolders([]);
    setError('');
    
    // Force complete state refresh with new key
    setRefreshKey(prev => prev + 1);
    
    try {
      await loadDocumentBases(); // This will also load all folders
      if (selectedFolder) {
        await loadDocuments();
      }
      setSuccessMessage('Data refreshed successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      console.log('[REFRESH] Refresh complete');
    } catch (err) {
      console.error('[REFRESH] Error during refresh:', err);
      setError('Failed to refresh data: ' + err?.message);
    }
  };

  const filteredDocuments = documents?.filter((doc) =>
    doc?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Add storage configuration handler with proper strategy switching
  const handleStorageConfiguration = (config) => {
    console.log('[STORAGE] Configuration updated:', config);
    
    // CRITICAL: Update document service storage strategy
    if (config?.storageStrategy) {
      documentService?.setStorageStrategy(config?.storageStrategy);
      setCurrentStorageStrategy(config?.storageStrategy);
      console.log('[STORAGE] Document service strategy set to:', config?.storageStrategy);
    }
    
    if (config?.storageStrategy === 'sharepoint') {
      setSuccessMessage(`Storage configured: SharePoint (${config?.site?.displayName || 'Configured'})`);
    } else {
      setSuccessMessage('Storage configured: Supabase');
    }
    
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Add toolbar button to show storage info
  const StorageIndicator = () => {
    const strategy = documentService?.getStorageStrategy();
    
    return (
      <button
        onClick={() => setShowStorageConfigModal(true)}
        className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm font-medium"
      >
        <Icon 
          name={strategy === 'sharepoint' ? 'Cloud' : 'Database'} 
          size={16} 
        />
        <span>
          {strategy === 'sharepoint' 
            ? `SharePoint: ${selectedDrive?.name || 'Configured'}` 
            : 'Supabase Storage'}
        </span>
        <Icon name="Settings" size={14} />
      </button>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mb-4 mx-auto" />
          <p className="text-muted-foreground">Loading ZentriDMS...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // AuthContext will redirect to login
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <div className="flex pt-16">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-4 mx-6 mt-6 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} />
                <span className="text-sm">{error}</span>
                <button 
                  onClick={() => setError('')}
                  className="ml-auto hover:bg-destructive/20 rounded p-1"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="bg-success/10 border border-success/20 text-success px-4 py-3 mb-4 mx-6 mt-6 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                <span className="text-sm">{successMessage}</span>
                <button 
                  onClick={() => setSuccessMessage('')}
                  className="ml-auto hover:bg-success/20 rounded p-1"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="flex h-[calc(100vh-4rem)]" key={refreshKey}>
            {/* Document Tree Panel */}
            <div className="w-80 hidden lg:block">
              <DocumentBaseTree
                documentBases={documentBases?.map(base => ({ ...base, type: 'base' }))}
                folders={folders}
                selectedItem={selectedItem}
                onItemSelect={handleItemSelect}
                onCreateFolder={handleCreateFolder}
                onDeleteItem={handleDeleteItem}
                onRenameItem={handleRenameItem}
                onToggleVisibility={handleToggleVisibility}
              />
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-border p-4 flex items-center justify-between">
                <StorageIndicator />
                <DocumentToolbar
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  onUpload={() => setShowUploadModal(true)}
                  onCreateFolder={() => handleCreateFolder('folder', selectedItem)}
                  onBulkDelete={handleBulkDelete}
                  onRefresh={handleRefresh}
                  selectedDocuments={selectedDocuments}
                  currentFolder={selectedItem}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                />
              </div>

              <div className="flex-1 overflow-auto p-6">
                {selectedItem ? (
                  <>
                    <div className="mb-6">
                      <h1 className="text-2xl font-semibold text-foreground mb-2">
                        {selectedItem?.name}
                      </h1>
                      {selectedItem?.description && (
                        <p className="text-muted-foreground">{selectedItem?.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span>{filteredDocuments?.length} documents</span>
                        <span>•</span>
                        <span>
                          Last modified: {selectedItem?.updatedAt ? 
                            new Date(selectedItem?.updatedAt)?.toLocaleDateString() : 'Never'}
                        </span>
                        {selectedItem?.type === 'base' && (
                          <>
                            <span>•</span>
                            <span className={selectedItem?.isPublic ? 'text-success' : 'text-warning'}>
                              {selectedItem?.isPublic ? 'Public' : 'Private'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {filteredDocuments?.length > 0 ? (
                      <DocumentGrid
                        documents={filteredDocuments}
                        viewMode={viewMode}
                        onDocumentSelect={handleDocumentSelect}
                        onDocumentDelete={handleDocumentDelete}
                        onDocumentEdit={handleDocumentEdit}
                        onDocumentRename={handleDocumentRename}
                        selectedDocuments={selectedDocuments}
                        onSelectionChange={setSelectedDocuments}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                          <Icon name="FolderOpen" size={32} className="text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">
                          No documents found
                        </h3>
                        <p className="text-muted-foreground mb-6 max-w-md">
                          {searchQuery ? 
                            `No documents match your search for "${searchQuery}"` :
                            'This folder is empty. Upload documents or create subfolders to get started.'
                          }
                        </p>
                        <Button
                          variant="default"
                          iconName="Upload"
                          iconPosition="left"
                          onClick={() => setShowUploadModal(true)}
                        >
                          Upload Documents
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                      <Icon name="Database" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Get Started with Document Bases
                    </h3>
                    <p className="text-muted-foreground max-w-md mb-6">
                      Create your first document base to organize and manage your documents effectively.
                    </p>
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => handleCreateFolder('base', null)}
                    >
                      Create Document Base
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Modals */}
      <DocumentUpload
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
        targetFolder={selectedItem}
      />
      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateItem}
        type={createModalType}
        parentFolder={createModalType === 'folder' ? selectedItem : null}
      />
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal?.isOpen || false}
        onClose={() => setConfirmModal({ isOpen: false, type: '', item: null, items: [], loading: false })}
        onConfirm={confirmDelete}
        title={
          confirmModal?.type === 'bulk-delete' 
            ? `Delete ${confirmModal?.items?.length || 0} Documents`
            : `Delete ${confirmModal?.item?.type === 'base' ? 'Document Base' : 'Document'}`
        }
        message={
          confirmModal?.type === 'bulk-delete'
            ? `Are you sure you want to delete ${confirmModal?.items?.length || 0} document${confirmModal?.items?.length !== 1 ? 's' : ''}? This action cannot be undone and will permanently remove ${confirmModal?.items?.length === 1 ? 'this file' : 'these files'}.`
            : confirmModal?.item?.type === 'base' 
              ? `Are you sure you want to delete the document base "${confirmModal?.item?.name || ''}"? This will permanently remove the document base and cannot be undone.`
              : `Are you sure you want to delete the document "${confirmModal?.item?.name || ''}"? This will permanently remove the file and cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={confirmModal?.loading || false}
      >
        {confirmModal?.item?.type === 'base' && (
          <div className="bg-muted/50 border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Info" size={14} />
              <span>Only empty document bases can be deleted.</span>
            </div>
          </div>
        )}
        {confirmModal?.type === 'bulk-delete' && confirmModal?.items?.length > 0 && (
          <div className="bg-muted/50 border border-border rounded-lg p-3 mt-3">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="AlertTriangle" size={14} className="mt-0.5" />
              <div>
                <p className="font-medium text-foreground mb-1">Documents to be deleted:</p>
                <ul className="list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                  {confirmModal?.items?.slice(0, 5)?.map(doc => (
                    <li key={doc?.id} className="truncate">{doc?.name}</li>
                  ))}
                  {confirmModal?.items?.length > 5 && (
                    <li className="text-muted-foreground italic">
                      and {confirmModal?.items?.length - 5} more...
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </ConfirmationModal>
      {/* Rename Modal */}
      <RenameModal
        isOpen={renameModal?.isOpen}
        onClose={() => setRenameModal({ isOpen: false, item: null, loading: false })}
        onConfirm={confirmRename}
        itemName={renameModal?.item?.name || ''}
        itemType={renameModal?.item?.type === 'base' ? 'Document Base' : 'Document'}
        loading={renameModal?.loading}
      />
      {/* SharePoint Configuration Modal */}
      <SharePointConfigModal
        isOpen={showStorageConfigModal}
        onClose={() => setShowStorageConfigModal(false)}
        onConfigured={handleStorageConfiguration}
      />
    </div>
  );
};

export default DocumentBaseManagement;